import { useCallback, useEffect, useState } from "react";
import { FilePdf, Download, LinkSimple, Play, EnvelopeSimple, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { openPdf } from "../../pages/platform/ResellerDetailPage";

const ORDER = ["download", "video", "template", "link"];
const ICON = { download: FilePdf, video: Play, template: EnvelopeSimple, link: LinkSimple };

const copyText = async (text, what) => {
  try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error("Couldn't copy"); }
};

function TemplateCard({ item }) {
  const [open, setOpen] = useState(false);
  const r = item.rendered || { subject: item.subject, body: item.body };
  const mailto = `mailto:?subject=${encodeURIComponent(r.subject || "")}&body=${encodeURIComponent(r.body || "")}`;
  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-2" data-testid={`resource-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div><p className="text-sm font-semibold">{item.title}</p><p className="text-[11px] text-muted-foreground">{item.description}</p></div>
        <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)} data-testid={`resource-toggle-${item.id}`}>{open ? "Hide" : "Preview"}</Button>
      </div>
      {open && (
        <div className="rounded-md bg-muted/40 p-3 text-xs space-y-2" data-testid={`resource-preview-${item.id}`}>
          {r.subject && <p><span className="text-muted-foreground">Subject:</span> <b>{r.subject}</b></p>}
          <pre className="whitespace-pre-wrap font-sans leading-relaxed">{r.body}</pre>
          <p className="text-[10px] text-muted-foreground">Your code, link and contact details are filled in. Replace the [bracketed] prompts before sending.</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => copyText(r.body, "E-mail text")} data-testid={`resource-copy-${item.id}`}><Copy size={13} className="mr-1" /> Copy text</Button>
        {r.subject && <Button size="sm" variant="outline" onClick={() => copyText(r.subject, "Subject")} data-testid={`resource-copy-subject-${item.id}`}><Copy size={13} className="mr-1" /> Copy subject</Button>}
        <a href={mailto} className="inline-flex items-center h-8 rounded-md border border-input px-3 text-xs font-medium hover:bg-muted" data-testid={`resource-mailto-${item.id}`}><EnvelopeSimple size={13} className="mr-1" /> Open in my mail app</a>
      </div>
    </div>
  );
}

function ItemCard({ item, downloadBase }) {
  const Icon = ICON[item.category] || LinkSimple;
  return (
    <div className="rounded-lg border border-border bg-card p-4 flex items-start gap-3" data-testid={`resource-${item.id}`}>
      <span className="h-9 w-9 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0"><Icon size={18} weight="duotone" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{item.title}</p>
        <p className="text-[11px] text-muted-foreground">{item.description}</p>
        {item.kind === "file" && item.filename && <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{item.filename}{item.size ? ` · ${(item.size / 1024).toFixed(0)} KB` : ""}</p>}
      </div>
      {item.kind === "link" ? (
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-8 rounded-md border border-input px-3 text-xs font-medium hover:bg-muted shrink-0" data-testid={`resource-open-${item.id}`}><ArrowSquareOut size={13} className="mr-1" /> Open</a>
      ) : (
        <Button size="sm" variant="outline" onClick={() => openPdf(`${downloadBase}/${item.id}/download`)} className="shrink-0" data-testid={`resource-download-${item.id}`}><Download size={13} className="mr-1" /> Download</Button>
      )}
    </div>
  );
}

/* Resources hub as resellers see it: grouped by category; templates come pre-filled with the reseller's code and details. */
export function ResourcesHub({ endpoint = "/reseller-portal/resources", downloadBase = "/reseller-portal/resources" }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await API.get(endpoint); setData(data); } catch { toast.error("Couldn't load resources"); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const groups = ORDER.map((k) => [k, data.resources.filter((r) => r.category === k)]).filter(([, items]) => items.length);
  return (
    <div className="space-y-6" data-testid="resources-hub">
      {data.code && <p className="text-xs text-muted-foreground" data-testid="resources-code-note">Templates and the one-pager use your best code <b className="font-mono">{data.code.code}</b> ({data.code.discount_pct}% off).</p>}
      {groups.map(([k, items]) => (
        <section key={k} className="space-y-2" data-testid={`resources-group-${k}`}>
          <h3 className="text-sm font-semibold">{data.categories[k]}</h3>
          <div className="grid gap-3 lg:grid-cols-2 items-start">
            {items.map((item) => item.kind === "template" ? <TemplateCard key={item.id} item={item} /> : <ItemCard key={item.id} item={item} downloadBase={downloadBase} />)}
          </div>
        </section>
      ))}
      {!groups.length && <p className="text-sm text-muted-foreground" data-testid="resources-empty">Nothing here yet — the Fixlr team will add resources soon.</p>}
    </div>
  );
}
